/**
 * Hook for saving session data
 */
import { useCallback, useRef, useEffect } from "react";
import type { UpdateEvent } from "../../../types/domain";
import { uploadJson, saveSession } from "../api";
import { parseTranscriptToEvents } from "../lib/transcriptParser";
import { isRealSessionId } from "../constants";

type UseSaveSessionOptions = {
  API: string;
  effectiveSid: string | undefined;
  mintNewSessionId: () => string;
  selectedSessionId?: string;
  selectedPatientId?: string;
  therapistId?: string;
  authedFetch: (url: string, init?: RequestInit) => Promise<Response>;
  bubbles: UpdateEvent[];
  summary: string;
  sessionDate: string;
  similarSessionIds: string[];
  assessmentIds: string[];
  extraNotes: string;
  setBusy: (busy: boolean) => void;
  setError: (msg: string | null) => void;
  setSummary: (s: string) => void;
  setSessionId?: (sid: string | null) => void;
  setSessionDate: (d: string) => void;
  setEvents: React.Dispatch<React.SetStateAction<UpdateEvent[]>>;
  setLatestSeq: (seq: number) => void;
  setShowTranscript: (show: boolean) => void;
  setTranscriptMode: (mode: "history" | "live") => void;
  onEffectiveSessionId?: (sid?: string) => void;
  onSaved?: (() => void) | ((sid: string) => void);
  summaryRef: React.MutableRefObject<{ getDraft?: () => string; setDraft?: (s: string) => void } | null>;
  latestSeqRef: React.MutableRefObject<number>;
};

function fireOnSaved(cb: UseSaveSessionOptions["onSaved"], sid: string) {
  if (!cb) return;
  try {
    if ((cb as any).length >= 1) (cb as any)(sid);
    else (cb as any)();
  } catch {
    // ignore
  }
}

export function useSaveSession(opts: UseSaveSessionOptions) {
  const {
    API,
    effectiveSid,
    mintNewSessionId,
    selectedSessionId,
    selectedPatientId,
    therapistId,
    authedFetch,
    bubbles,
    summary,
    sessionDate,
    similarSessionIds,
    assessmentIds,
    extraNotes,
    setBusy,
    setError,
    setSummary,
    setSessionId,
    setSessionDate,
    setEvents,
    setLatestSeq,
    setShowTranscript,
    setTranscriptMode,
    onEffectiveSessionId,
    onSaved,
    summaryRef,
    latestSeqRef,
  } = opts;

  const fetchRef = useRef(authedFetch);
  useEffect(() => {
    fetchRef.current = authedFetch;
  }, [authedFetch]);

  const saveTranscript = useCallback(async () => {
    try {
      if (!API) throw new Error("Backend URL is not configured.");
      const therapist = therapistId;
      if (!therapist) throw new Error("Missing therapist id.");
      setError(null);
      setBusy(true);

      const draftSummary = summaryRef.current?.getDraft?.() ?? summary ?? "";
      const transcriptText = (bubbles || [])
        .map((b) => `${b.speaker || "?"}: ${(b.text ?? "").trim()}`)
        .filter((l) => l.length > 0)
        .join("\n");

      const starts = bubbles.map((b) => Number((b as any).start_sec)).filter((v) => Number.isFinite(v));
      const ends = bubbles.map((b) => Number((b as any).end_sec)).filter((v) => Number.isFinite(v));
      const duration_sec = starts.length && ends.length ? Math.max(...ends) - Math.min(...starts) : undefined;

      const sid = effectiveSid ?? mintNewSessionId();
      const body = {
        patient_id: selectedPatientId ?? null,
        therapist_id: therapist,
        transcript: transcriptText,
        summary: draftSummary,
        lang: "en",
        duration_sec: typeof duration_sec === "number" ? duration_sec : null,
        session_date: sessionDate || null,
        title: sessionDate || null,
        similar_session_ids: similarSessionIds,
        assessment_ids: assessmentIds,
        extra_notes: extraNotes || null,
      };

      const result = await saveSession(fetchRef.current, API, sid, body);

      // Backend may redirect to an existing session (1 session per day per patient)
      const actualSid = result?.session_id || sid;

      setSummary(draftSummary);

      setSessionId?.(actualSid);
      onEffectiveSessionId?.(actualSid);

      window.dispatchEvent(new Event("tj:sessions:refresh"));
      fireOnSaved(onSaved, actualSid);
    } catch (e: any) {
      console.error("[useSaveSession] saveTranscript failed:", e);
      setError(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  }, [
    API,
    effectiveSid,
    mintNewSessionId,
    bubbles,
    summary,
    selectedPatientId,
    sessionDate,
    therapistId,
    similarSessionIds,
    assessmentIds,
    extraNotes,
    setBusy,
    setError,
    onSaved,
    setSummary,
    setSessionId,
    onEffectiveSessionId,
    summaryRef,
  ]);

  const submitJson = useCallback(
    async (file?: File) => {
      if (!file) return;
      try {
        if (!selectedPatientId) throw new Error("Select a patient to upload JSON.");
        if (!API) throw new Error("Backend URL is not configured.");

        const sid = isRealSessionId(selectedSessionId) ? selectedSessionId! : mintNewSessionId();

        // Backend requires session_date on session creation (monthly cap
        // counts by date). Use the panel's session date, or today's date —
        // the same date the generated title already displays.
        const uploadDate = sessionDate || new Date().toISOString().slice(0, 10);

        await uploadJson(
          fetchRef.current,
          API,
          sid,
          file,
          selectedPatientId,
          `Transcript ${new Date().toISOString().slice(0, 10)}`,
          uploadDate
        );

        setSessionId?.(sid);
        onEffectiveSessionId?.(sid);

        const resp = await fetchRef.current(`${API}/sessions/${encodeURIComponent(sid)}`, { method: "GET" });
        const raw = await resp.text();
        if (!resp.ok) throw new Error(raw || resp.statusText);
        const data = raw ? JSON.parse(raw) : {};
        const item = data?.item || data || {};

        const loadedDate =
          (typeof item.session_date === "string" && item.session_date) ? item.session_date :
          (typeof item.sessionDate === "string" && item.sessionDate) ? item.sessionDate :
          (typeof item.date === "string" && item.date) ? item.date :
          "";
        if (loadedDate) setSessionDate(loadedDate);

        // Prefer raw_transcript (NDJSON with timing) over transcript (plain text)
        const transcriptText: string | undefined =
          typeof item.raw_transcript === "string" && item.raw_transcript.trim()
            ? item.raw_transcript
            : typeof item.transcript === "string"
            ? item.transcript
            : undefined;

        if (transcriptText && transcriptText.trim()) {
          const evs = parseTranscriptToEvents(transcriptText);
          setEvents(evs as any);
          if (evs.length) {
            const maxSeq = Math.max(...evs.map((x: any) => Number(x.seq || 0)));
            setLatestSeq(maxSeq);
            latestSeqRef.current = maxSeq;
          }
          setShowTranscript(true);
          setTranscriptMode("history");
        }

        window.dispatchEvent(new Event("tj:sessions:refresh"));
      } catch (e: any) {
        setError(e?.message || String(e));
      }
    },
    [
      API,
      selectedSessionId,
      selectedPatientId,
      sessionDate,
      mintNewSessionId,
      setError,
      setEvents,
      setLatestSeq,
      setSessionId,
      setSessionDate,
      setShowTranscript,
      setTranscriptMode,
      onEffectiveSessionId,
      latestSeqRef,
    ]
  );

  return {
    saveTranscript,
    submitJson,
  };
}
